import React, {useEffect} from 'react';
import {Image, Pressable, StyleSheet, View} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import {ToastPayload} from './types';
import CCColors from '@/Utils/CCColors';
import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';

type Props = {
  toast: ToastPayload;
  index: number;
  onHide: () => void;
};

export const ToastItem: React.FC<Props> = ({toast, index, onHide}) => {
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(-16);
  const scale = useSharedValue(1);

  useEffect(() => {
    opacity.value = withTiming(1, {duration: 250});
    translateY.value = withTiming(0, {duration: 250});

    const timer = setTimeout(() => {
      opacity.value = withTiming(0, {duration: 20000}, () => {
        runOnJS(onHide)();
      });
    }, toast.duration);

    return () => clearTimeout(timer);
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      {translateY: translateY.value + index * 12},
      {scale: scale.value},
    ],
  }));

  return (
    <Animated.View style={[styles.wrapper, animatedStyle]}>
      <Pressable style={styles.toast} onPress={toast.onPress}>
        <View style={styles.iconWrapper}>
          <Image
            style={{width: 20, height: 20}}
            source={require('../Assets/Images/Svgs/logo/logo.svg')}
          />
        </View>

        <View style={styles.textContainer}>
          <CCText
            fontSize={FontSizes[14]}
            color={CCColors?.TextColor?.LightGrey}
            style={styles.title}>
            {'New ' + toast.type}
          </CCText>
          <CCText
            fontSize={FontSizes[14]}
            color={CCColors?.TextColor?.Primary}
            style={styles.message}
            numberOfLines={1}
            showEllipses>
            {toast.message}
          </CCText>
        </View>

        <Pressable
          onPress={e => {
            e?.stopPropagation?.();
            (toast.onClose ?? onHide)();
          }}
          hitSlop={10}
          style={styles.closeButton}>
          <MaterialIcons
            name="close"
            size={13}
            color={CCColors?.TextColor?.LightGrey}
          />
        </Pressable>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    alignItems: 'center',
    pointerEvents: 'box-none',
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors?.White,
    borderWidth: 1,
    borderColor: CCColors?.Red,
    borderRadius: 8,
    padding: 12,
    minWidth: 340,
    maxWidth: 340,
    marginTop: 8,
    pointerEvents: 'auto',
    shadowColor: CCColors?.Black,
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 4,
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 20,
    padding: 10,
    backgroundColor: CCColors?.Primary?.lightBrown,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    marginBottom: 2,
    fontWeight: '400',
  },
  message: {
    fontWeight: '600',
  },
  closeButton: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.25,
    borderRadius: 8,
    borderColor: CCColors?.TextColor?.LightGrey,
    position: 'absolute',
    top: 8,
    right: 8,
  },
});
